import type { HaulerRouteStop, HaulerRouteView } from "@/lib/logistics/haulerTypes";
import { buildMapsUrl, buildTelUrl } from "@/lib/driver/driverMapLinks";

function addressLine(address: Record<string, unknown>): string {
  const parts = ["line1", "line2", "city", "state", "postal_code", "zip"]
    .map((k) => address[k])
    .filter((v): v is string => typeof v === "string" && v.trim().length > 0);
  return parts.join(", ") || "Address on file";
}

function routeHeading(route: HaulerRouteView): string {
  const when = route.scheduled_window_label?.trim()
    ? route.scheduled_window_label
    : route.scheduled_for
      ? new Date(route.scheduled_for).toLocaleString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
      : "Unscheduled";
  return `${route.unit_label} · ${when}`;
}

function StopRow({ stop }: { stop: HaulerRouteStop }) {
  const mapsUrl = buildMapsUrl(stop.address);
  const telUrl = buildTelUrl(stop.contact_phone);
  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
      <p className="text-xs font-semibold uppercase text-slate-500">
        Stop {stop.sequence_index + 1} · {stop.stop_kind.replace(/_/g, " ")}
      </p>
      <p className="text-sm text-slate-800 dark:text-slate-100">{addressLine(stop.address)}</p>
      {stop.contact_name ? <p className="text-sm text-slate-600">{stop.contact_name}</p> : null}
      {stop.notes ? <p className="mt-1 text-xs italic text-slate-600">{stop.notes}</p> : null}
      <div className="mt-2 flex gap-2 text-sm">
        {mapsUrl ? (
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">
            Maps
          </a>
        ) : null}
        {telUrl ? (
          <a href={telUrl} className="text-emerald-700 underline">
            Call
          </a>
        ) : null}
      </div>
    </li>
  );
}

type Props = {
  carrierName: string;
  routes: HaulerRouteView[];
};

export function HaulerRouteList({ carrierName, routes }: Props) {
  if (!routes.length) {
    return (
      <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-600">
        No active routes assigned to {carrierName}.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {routes.map((route) => (
        <section key={route.id} className="space-y-2">
          <header>
            <h2 className="text-lg font-bold">{routeHeading(route)}</h2>
            <p className="text-xs uppercase tracking-wide text-slate-500">
              {route.route_state.replace(/_/g, " ")}
              {route.driver_name ? ` · ${route.driver_name}` : ""}
            </p>
            {route.notes ? <p className="text-sm text-slate-600">{route.notes}</p> : null}
          </header>
          <ul className="space-y-2">
            {route.stops.map((stop) => (
              <StopRow key={stop.id} stop={stop} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
